import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Button,
  Table,
  Badge,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faEye,
  faTrash,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import Results from './Results';
import { useHistory } from 'react-router-dom';
// Import the API functions
import { getSavedMaintenancePlans, getMaintenancePlanById, downloadMaintenancePlanById } from '../../../service/workflow-api';

const SavedPlans = ({ onBack }) => {
  const [savedPlans, setSavedPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(false);
  const [error, setError] = useState(null);
  const history = useHistory();

  useEffect(() => {
    // Fetch saved plans from API
    const fetchSavedPlans = async () => {
      try {
        const response = await getSavedMaintenancePlans();
        if (response && Array.isArray(response)) {
          setSavedPlans(response);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching saved plans:", err);
        setError("Failed to load saved plans");
      } finally {
        setLoading(false);
      }
    };

    fetchSavedPlans();
  }, []);

  const handleViewPlan = async (planId) => {
    setLoading(true);
    try {
      const response = await getMaintenancePlanById(planId);
      if (response && response.output_json) {
        setSelectedPlan({
          id: planId,
          data: response.output_json
        });
        setViewingPlan(true);
      } else {
        throw new Error("Invalid response format or missing output_json");
      }
    } catch (err) {
      console.error("Error fetching plan details:", err);
      setError("Failed to load plan details");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewingPlan(false);
    setSelectedPlan(null);
  };

  const handleBackToPlanner = () => {
    if (onBack) {
      onBack();
    } else {
      // Use React Router history to navigate back to the main planner
      history.push('/ai-workflows/ai-predictive-maintenance-planner');
    }
  };

  const handleDeletePlan = async (planId) => {
    // In a real app, this would call an API to delete the plan
    // For now, just remove it from the UI
    const updatedPlans = savedPlans.filter(plan => plan.id !== planId);
    setSavedPlans(updatedPlans);
    alert(`Plan ${planId} has been deleted`);
  };

  const handleDownloadPlan = async (planId) => {
    try {
      const response = await downloadMaintenancePlanById(planId);
      if (response && response.ok) {
        // Get filename from Content-Disposition header if available
        let filename = `maintenance_plan_${planId}.xlsx`;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Download the file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error("Failed to download plan");
      }
    } catch (err) {
      console.error("Error downloading plan:", err);
      alert("Failed to download maintenance plan");
    }
  };

  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading saved maintenance plans...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Card className="dark-main-card text-white mb-4">
          <Card.Body className="p-4 text-center">
            <FontAwesomeIcon 
              icon={faExclamationTriangle} 
              style={{ fontSize: '3rem', color: '#dc3545', marginBottom: '20px' }} 
            />
            <h5 className="text-danger mb-3">Error</h5>
            <p className="text-white mb-4">{error}</p>
            <Button 
              variant="outline-light" 
              size="sm"
              onClick={handleBackToPlanner}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Planner
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (viewingPlan && selectedPlan) {
    return <Results data={selectedPlan.data} fromSaved={true} onBack={handleBackToList} />;
  }

  return (
    <Container fluid style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <Card className="dark-main-card text-white mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 style={{ color: '#d9d9d9', marginBottom: '5px' }}>
                Saved Maintenance Plans
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.9em' }}>
                {savedPlans.length} {savedPlans.length === 1 ? 'plan' : 'plans'} saved
              </p>
            </div>
            <Button 
              variant="outline-light" 
              size="sm"
              style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.15)' }}
              onClick={handleBackToPlanner}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Planner
            </Button>
          </div>
          
          {savedPlans.length === 0 ? (
            <div className="text-center p-5">
              <FontAwesomeIcon 
                icon={faExclamationTriangle} 
                style={{ fontSize: '3rem', color: '#adb5bd', marginBottom: '20px' }} 
              />
              <h5 className="text-muted">No Saved Items Found</h5>
              <p className="text-muted mb-4">
                You haven't saved any maintenance plans yet. 
                Create a new plan and save it to see it here.
              </p>
              <Button 
                variant="primary" 
                size="sm"
                onClick={onBack}
              >
                Create New Plan
              </Button>
            </div>
          ) : (
            <Table 
              responsive 
              variant="dark" 
              striped 
              className="mt-3"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <thead>
                <tr>
                  <th>Plan Name</th>
                  <th>Venue</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <strong>{plan.name || `Maintenance Plan ${plan.id}`}</strong>
                    </td>
                    <td>{plan.venue_name || "Not specified"}</td>
                    <td>{new Date(plan.created_at).toLocaleDateString()}</td>
                    <td>
                      <Button 
                        variant="outline-light" 
                        size="sm" 
                        className="me-2"
                        style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                        onClick={() => handleViewPlan(plan.id)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        className="me-2"
                        style={{ background: 'rgba(220, 53, 69, 0.05)', border: '1px solid rgba(220, 53, 69, 0.2)' }}
                        onClick={() => handleDeletePlan(plan.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SavedPlans;